import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronRight,
  Lock,
  Plus,
  RotateCcw,
  Save,
  Shield,
  Trash2,
  X,
} from "lucide-react";
import Button from "../ui/Button";
import usePagination from "../../hooks/usePagination";
import { showError, showInfo, showSuccess } from "../../utils/toast";
import ListPagination from "./ListPagination";

const DEFAULT_ROLES_PAGE_SIZE = 5;
const DEFAULT_PERMISSION_GROUPS_PAGE_SIZE = 4;

function ConfirmModal({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "danger",
  onConfirm,
  onClose,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const confirmClass =
    tone === "danger"
      ? "bg-red-600 text-white hover:bg-red-700 shadow-md shadow-red-600/20"
      : undefined;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-primary/40 p-4 backdrop-blur-sm sm:items-center"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-title"
        className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-primary/[0.06] px-5 py-4">
          <h2 id="confirm-modal-title" className="text-lg font-bold text-primary">
            {title}
          </h2>
          <p className="mt-1 text-sm text-primary/55">{message}</p>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4">
          <Button variant="ghost" size="sm" onClick={onClose}>
            {cancelLabel}
          </Button>
          <Button
            size="sm"
            className={confirmClass}
            onClick={() => {
              onConfirm();
              onClose();
            }}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

function CreateRoleModal({ onClose, onCreate, permissionGroups = [] }) {
  const [label, setLabel] = useState("");
  const [draft, setDraft] = useState([]);
  const [openGroups, setOpenGroups] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setOpenGroups(
      Object.fromEntries(permissionGroups.map((group, index) => [group.label, index === 0]))
    );
  }, [permissionGroups]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape" && !submitting) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);

  const toggle = (key) => {
    setDraft((prev) =>
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
    );
  };

  const toggleGroup = (groupKeys, enable) => {
    setDraft((prev) => {
      if (enable) return [...new Set([...prev, ...groupKeys])];
      return prev.filter((key) => !groupKeys.includes(key));
    });
  };

  const canSubmit = Boolean(label.trim()) && draft.length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) {
      if (!label.trim()) showError("Enter a role name.");
      else if (!draft.length) showError("Select at least one permission to create a role.");
      return;
    }
    setSubmitting(true);
    try {
      await onCreate({ label: label.trim(), permissions: draft });
    } catch (err) {
      showError(err?.message || "Could not create role.");
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-primary/40 p-4 backdrop-blur-sm sm:items-center"
      onClick={() => {
        if (!submitting) onClose();
      }}
    >
      <div
        role="dialog"
        aria-labelledby="create-role-title"
        className="flex max-h-[min(92vh,720px)] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between border-b border-primary/[0.06] px-5 py-4">
          <div>
            <h2 id="create-role-title" className="text-lg font-bold text-primary">
              Add custom role
            </h2>
            <p className="mt-1 text-sm text-primary/45">
              Name the role and assign at least one permission. Roles cannot be created without
              permissions.
            </p>
          </div>
          <button
            type="button"
            disabled={submitting}
            onClick={onClose}
            className="rounded-lg p-2 text-primary/40 hover:bg-primary/5 disabled:opacity-40"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-5">
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-primary/50">Role name</label>
            <input
              autoFocus
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. HOD"
              disabled={submitting}
              className="w-full rounded-lg border border-primary/10 px-3 py-2.5 text-sm focus:border-accent/50 focus:outline-none focus:ring-2 focus:ring-accent/20 disabled:opacity-60"
            />
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-primary/35">
                Permissions ({draft.length} selected)
              </p>
            </div>
            <div className="space-y-2">
              {permissionGroups.map((group) => {
                const groupKeys = group.permissions.map((p) => p.key);
                const enabledInGroup = groupKeys.filter((key) => draft.includes(key)).length;
                const allOn = enabledInGroup === groupKeys.length && groupKeys.length > 0;
                const noneOn = enabledInGroup === 0;
                const isOpen = Boolean(openGroups[group.label]);

                return (
                  <section
                    key={group.label}
                    className="overflow-hidden rounded-xl border border-primary/[0.07] bg-white"
                  >
                    <div className="flex items-center gap-2 px-3 py-2">
                      <button
                        type="button"
                        onClick={() =>
                          setOpenGroups((prev) => ({
                            ...prev,
                            [group.label]: !prev[group.label],
                          }))
                        }
                        className="flex min-w-0 flex-1 items-center gap-2 text-left"
                      >
                        <ChevronDown
                          className={`h-4 w-4 shrink-0 text-primary/35 transition-transform ${
                            isOpen ? "rotate-0" : "-rotate-90"
                          }`}
                        />
                        <span className="min-w-0">
                          <span className="block text-sm font-bold text-primary">{group.label}</span>
                          <span className="text-[10px] text-primary/40">
                            {enabledInGroup}/{groupKeys.length} enabled
                          </span>
                        </span>
                      </button>
                      <div className="flex shrink-0 gap-1">
                        <button
                          type="button"
                          onClick={() => toggleGroup(groupKeys, true)}
                          disabled={allOn || submitting}
                          className="rounded-md px-2 py-1 text-[10px] font-semibold text-primary/50 hover:bg-primary/[0.04] disabled:opacity-35"
                        >
                          All
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleGroup(groupKeys, false)}
                          disabled={noneOn || submitting}
                          className="rounded-md px-2 py-1 text-[10px] font-semibold text-primary/50 hover:bg-primary/[0.04] disabled:opacity-35"
                        >
                          None
                        </button>
                      </div>
                    </div>
                    {isOpen && (
                      <div className="border-t border-primary/[0.05] px-2 py-2">
                        <div className="flex flex-wrap gap-1.5">
                          {group.permissions.map(({ key, label: permLabel }) => {
                            const on = draft.includes(key);
                            return (
                              <button
                                key={key}
                                type="button"
                                title={key}
                                disabled={submitting}
                                onClick={() => toggle(key)}
                                className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-left transition-all ${
                                  on
                                    ? "border-accent/40 bg-accent/[0.08] text-primary"
                                    : "border-primary/[0.07] bg-primary/[0.015] text-primary/55 hover:border-primary/15"
                                } ${submitting ? "cursor-default opacity-80" : "cursor-pointer"}`}
                              >
                                <span
                                  className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border ${
                                    on
                                      ? "border-accent bg-accent text-primary"
                                      : "border-primary/20 bg-white text-transparent"
                                  }`}
                                >
                                  <Check className="h-2.5 w-2.5" strokeWidth={3} />
                                </span>
                                <span className="text-[11px] font-semibold leading-tight">
                                  {permLabel}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t border-primary/[0.06] px-5 py-4">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button size="sm" disabled={!canSubmit} onClick={handleSubmit}>
            {submitting ? "Creating…" : "Create role"}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * List-first roles manager: click a role to edit name/permissions.
 * Supports adding custom roles. Locked roles (super_admin) are view-only.
 */
export default function RolesPermissionsPanel({
  roles,
  getRoleLabel,
  setRoleMeta,
  permissionGroups,
  getRolePermissions,
  setRolePermissions,
  resetRolePermissions,
  createRole,
  /** Optional async save — ({ role, label, permissions }) => Promise */
  saveRole,
  deleteRole,
  isRoleLocked,
  isSystemRole,
  subscribe,
  lockedRoles = [],
  countUsersForRole,
  canEdit = true,
  lockedNotice = "This role always has full access. Permissions cannot be changed.",
  rolesPageSize = DEFAULT_ROLES_PAGE_SIZE,
  permissionGroupsPageSize = DEFAULT_PERMISSION_GROUPS_PAGE_SIZE,
}) {
  const [view, setView] = useState("list");
  const [selectedRole, setSelectedRole] = useState(null);
  const [draft, setDraft] = useState([]);
  const [labelDraft, setLabelDraft] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [openGroups, setOpenGroups] = useState({});
  const [saving, setSaving] = useState(false);
  const [, bump] = useState(0);

  useEffect(() => {
    setOpenGroups(
      Object.fromEntries(permissionGroups.map((group, index) => [group.label, index === 0]))
    );
  }, [permissionGroups]);

  useEffect(() => {
    if (!subscribe) return undefined;
    return subscribe(() => bump((n) => n + 1));
  }, [subscribe]);

  const {
    page: rolesPage,
    totalPages: rolesTotalPages,
    paginatedItems: paginatedRoles,
    goToPage: goToRolesPage,
    totalItems: rolesTotalItems,
    startIndex: rolesStartIndex,
    endIndex: rolesEndIndex,
  } = usePagination(roles, rolesPageSize);

  const {
    page: groupsPage,
    totalPages: groupsTotalPages,
    paginatedItems: paginatedGroups,
    goToPage: goToGroupsPage,
    resetPage: resetGroupsPage,
    totalItems: groupsTotalItems,
    startIndex: groupsStartIndex,
    endIndex: groupsEndIndex,
  } = usePagination(permissionGroups, permissionGroupsPageSize);

  useEffect(() => {
    resetGroupsPage();
  }, [selectedRole]); // eslint-disable-line react-hooks/exhaustive-deps

  const openRole = (role) => {
    setSelectedRole(role);
    setDraft(getRolePermissions(role));
    setLabelDraft(getRoleLabel(role));
    setView("detail");
  };

  const backToList = () => {
    setView("list");
    setSelectedRole(null);
  };

  const locked =
    Boolean(selectedRole) &&
    (lockedRoles.includes(selectedRole) || isRoleLocked?.(selectedRole));

  const baseline = selectedRole ? getRolePermissions(selectedRole) : [];
  const baselineLabel = selectedRole ? getRoleLabel(selectedRole) : "";

  const dirtyPermissions =
    Boolean(selectedRole) &&
    !locked &&
    (draft.length !== baseline.length || draft.some((key) => !baseline.includes(key)));
  const dirtyMeta =
    Boolean(selectedRole) &&
    !locked &&
    Boolean(setRoleMeta) &&
    labelDraft.trim() !== baselineLabel;
  const dirty = dirtyPermissions || dirtyMeta;

  const selectedCount = draft.length;
  const totalCount = useMemo(
    () => permissionGroups.reduce((sum, group) => sum + group.permissions.length, 0),
    [permissionGroups]
  );

  const toggle = (key) => {
    if (!canEdit || locked) return;
    setDraft((prev) =>
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
    );
  };

  const toggleGroup = (groupKeys, enable) => {
    if (!canEdit || locked) return;
    setDraft((prev) => {
      if (enable) return [...new Set([...prev, ...groupKeys])];
      return prev.filter((key) => !groupKeys.includes(key));
    });
  };

  const handleSave = async () => {
    if (!selectedRole || locked || saving) return;
    const nextLabel = labelDraft.trim() || baselineLabel;
    if (!draft.length) {
      showError("A role must keep at least one permission.");
      return;
    }

    if (saveRole) {
      setSaving(true);
      try {
        await saveRole({
          role: selectedRole,
          label: nextLabel,
          permissions: draft,
        });
        showSuccess("Role saved. Members with this role are updated.");
        backToList();
      } catch (err) {
        showError(err?.message || "Could not save role.");
      } finally {
        setSaving(false);
      }
      return;
    }

    if (dirtyMeta && setRoleMeta) {
      setRoleMeta(selectedRole, { label: nextLabel });
    }
    if (dirtyPermissions || !dirtyMeta) {
      setRolePermissions(selectedRole, draft);
    }
    showSuccess("Role saved. Members with this role are updated.");
    backToList();
  };

  const handleResetConfirm = () => {
    if (!selectedRole || locked) return;
    const defaults = resetRolePermissions(selectedRole);
    setDraft(defaults);
    setLabelDraft(getRoleLabel(selectedRole));
    showInfo("Role reset to defaults.");
  };

  const handleCreate = async ({ label, permissions }) => {
    if (!createRole) return;
    if (!permissions?.length) {
      throw new Error("Select at least one permission to create a role.");
    }
    await createRole({ label, permissions });
    setCreateOpen(false);
    backToList();
    showSuccess(`Role “${label}” created.`);
  };

  const selectedUserCount = selectedRole ? countUsersForRole?.(selectedRole) ?? 0 : 0;
  const canDeleteSelected =
    Boolean(deleteRole) &&
    Boolean(selectedRole) &&
    !locked &&
    selectedUserCount === 0;

  const handleDeleteConfirm = async () => {
    if (!selectedRole || !deleteRole || locked) return;
    const userCount = countUsersForRole?.(selectedRole) ?? 0;
    if (userCount > 0) {
      showError(
        `Cannot delete “${getRoleLabel(selectedRole)}” — ${userCount} ${
          userCount === 1 ? "member is" : "members are"
        } still assigned. Reassign them first.`
      );
      return;
    }
    const name = getRoleLabel(selectedRole);
    try {
      const removed = await deleteRole(selectedRole);
      if (removed === false) {
        showError(`Cannot delete “${name}”.`);
        return;
      }
      backToList();
      showSuccess(`Role “${name}” deleted.`);
    } catch (err) {
      showError(err?.message || `Could not delete “${name}”.`);
    }
  };

  if (view === "list") {
    return (
      <>
        <div className="overflow-hidden rounded-2xl border border-primary/[0.08] bg-white shadow-[0_8px_30px_-18px_rgba(127,29,29,0.35)]">
          <div className="flex flex-wrap items-start justify-between gap-3 border-b border-primary/[0.06] bg-gradient-to-r from-primary/[0.03] to-accent/[0.05] px-4 py-3.5 sm:px-5">
            <div>
              <p className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-primary/45">
                <Shield className="h-3.5 w-3.5 text-accent" />
                Access control
              </p>
              <h3 className="mt-1 text-base font-bold text-primary">Roles</h3>
              <p className="mt-0.5 max-w-xl text-xs text-primary/45">
                Select a role to view or edit its permissions. A role can be deleted only when no
                members are assigned. Super Admin cannot be deleted.
              </p>
            </div>
            {canEdit && createRole && (
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4" />
                Add role
              </Button>
            )}
          </div>

          <ul className="divide-y divide-primary/[0.05]">
            {paginatedRoles.map((role) => {
              const roleLocked = lockedRoles.includes(role) || isRoleLocked?.(role);
              const userCount = countUsersForRole?.(role) ?? 0;
              const permCount = getRolePermissions(role).length;
              const system = isSystemRole?.(role);

              return (
                <li key={role}>
                  <button
                    type="button"
                    onClick={() => openRole(role)}
                    className="flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-primary/[0.02] sm:px-5"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/[0.05] text-primary/60">
                      {roleLocked ? <Lock className="h-4 w-4" /> : <Shield className="h-4 w-4" />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-bold text-primary">{getRoleLabel(role)}</span>
                        {roleLocked && (
                          <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-700">
                            Locked
                          </span>
                        )}
                        {!system && (
                          <span className="rounded bg-sky-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-sky-700">
                            Custom
                          </span>
                        )}
                      </span>
                      <span className="mt-1 block text-[10px] font-medium text-primary/35">
                        {userCount} {userCount === 1 ? "member" : "members"} · {permCount} permissions
                      </span>
                    </span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-primary/30" />
                  </button>
                </li>
              );
            })}
          </ul>

          <ListPagination
            page={rolesPage}
            totalPages={rolesTotalPages}
            totalItems={rolesTotalItems}
            startIndex={rolesStartIndex}
            endIndex={rolesEndIndex}
            goToPage={goToRolesPage}
            pageSize={rolesPageSize}
            alwaysShow
          />
        </div>

        {createOpen && (
          <CreateRoleModal
            onClose={() => setCreateOpen(false)}
            onCreate={handleCreate}
            permissionGroups={permissionGroups}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className="overflow-hidden rounded-2xl border border-primary/[0.08] bg-white shadow-[0_8px_30px_-18px_rgba(127,29,29,0.35)]">
        <div className="border-b border-primary/[0.06] bg-gradient-to-r from-primary/[0.03] to-accent/[0.05] px-4 py-3.5 sm:px-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <button
                type="button"
                onClick={backToList}
                className="mb-2 inline-flex items-center gap-1.5 text-xs font-semibold text-primary/50 transition-colors hover:text-primary"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                All roles
              </button>
              <h3 className="text-base font-bold text-primary">{getRoleLabel(selectedRole)}</h3>
              <p className="mt-0.5 text-xs text-primary/45">
                {locked ? "View only — this role cannot be changed." : "Edit the role name and permissions, then save."}
              </p>
            </div>
            {canEdit && !locked && (
              <div className="flex flex-wrap items-center gap-2">
                {deleteRole && (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!canDeleteSelected}
                    title={
                      selectedUserCount > 0
                        ? `Reassign ${selectedUserCount} ${
                            selectedUserCount === 1 ? "member" : "members"
                          } before deleting this role.`
                        : "Delete this role"
                    }
                    onClick={() => {
                      if (!canDeleteSelected) {
                        showError(
                          selectedUserCount > 0
                            ? `Cannot delete “${getRoleLabel(selectedRole)}” — reassign all members first.`
                            : `Cannot delete “${getRoleLabel(selectedRole)}”.`
                        );
                        return;
                      }
                      setConfirm({
                        type: "delete",
                        title: "Delete role",
                        message: `Delete “${getRoleLabel(
                          selectedRole
                        )}”? This role has no members and will be permanently removed.`,
                        confirmLabel: "Delete role",
                        tone: "danger",
                        onConfirm: handleDeleteConfirm,
                      });
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setConfirm({
                      type: "reset",
                      title: "Reset role",
                      message: `Reset “${getRoleLabel(selectedRole)}” to its default permissions? Unsaved changes will be lost.`,
                      confirmLabel: "Reset",
                      tone: "default",
                      onConfirm: handleResetConfirm,
                    })
                  }
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Reset
                </Button>
                <Button size="sm" onClick={handleSave} disabled={!dirty || saving || draft.length === 0}>
                  <Save className="h-3.5 w-3.5" />
                  {saving ? "Saving…" : "Save role"}
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="flex max-h-[min(72vh,760px)] flex-col">
          <div className="shrink-0 space-y-3 border-b border-primary/[0.06] p-4 sm:p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                {canEdit && !locked && setRoleMeta ? (
                  <div>
                    <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.08em] text-primary/40">
                      Role name
                    </label>
                    <input
                      value={labelDraft}
                      onChange={(e) => setLabelDraft(e.target.value)}
                      className="w-full max-w-md rounded-lg border border-primary/10 px-3 py-2 text-sm font-semibold text-primary focus:border-accent/50 focus:outline-none focus:ring-2 focus:ring-accent/20"
                    />
                  </div>
                ) : (
                  <h4 className="text-base font-bold text-primary">{getRoleLabel(selectedRole)}</h4>
                )}
              </div>
              <div className="rounded-lg border border-primary/[0.08] bg-primary/[0.02] px-3 py-1.5 text-right">
                <p className="text-[9px] font-bold uppercase tracking-[0.1em] text-primary/40">
                  Enabled
                </p>
                <p className="text-sm font-bold text-primary">
                  {selectedCount}
                  <span className="font-medium text-primary/40"> / {totalCount}</span>
                </p>
              </div>
            </div>

            {locked && (
              <div className="rounded-lg border border-amber-200/70 bg-amber-50/70 px-3 py-2 text-[11px] text-amber-900/80">
                {lockedNotice}
              </div>
            )}

            <div className="flex items-center justify-between gap-2">
              <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-primary/35">
                Permissions
              </p>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() =>
                    setOpenGroups(Object.fromEntries(permissionGroups.map((g) => [g.label, true])))
                  }
                  className="rounded-md px-2 py-1 text-[10px] font-semibold text-primary/45 hover:bg-primary/[0.04]"
                >
                  Expand all
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setOpenGroups(Object.fromEntries(permissionGroups.map((g) => [g.label, false])))
                  }
                  className="rounded-md px-2 py-1 text-[10px] font-semibold text-primary/45 hover:bg-primary/[0.04]"
                >
                  Collapse all
                </button>
              </div>
            </div>
          </div>

          <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-4 sm:p-5">
            {paginatedGroups.map((group) => {
              const groupKeys = group.permissions.map((p) => p.key);
              const enabledInGroup = groupKeys.filter((key) => draft.includes(key)).length;
              const allOn = enabledInGroup === groupKeys.length;
              const noneOn = enabledInGroup === 0;
              const isOpen = Boolean(openGroups[group.label]);

              return (
                <section
                  key={group.label}
                  className="overflow-hidden rounded-xl border border-primary/[0.07] bg-white"
                >
                  <div className="flex items-center gap-2 px-3 py-2">
                    <button
                      type="button"
                      onClick={() =>
                        setOpenGroups((prev) => ({ ...prev, [group.label]: !prev[group.label] }))
                      }
                      className="flex min-w-0 flex-1 items-center gap-2 text-left"
                    >
                      <ChevronDown
                        className={`h-4 w-4 shrink-0 text-primary/35 transition-transform ${
                          isOpen ? "rotate-0" : "-rotate-90"
                        }`}
                      />
                      <span className="min-w-0">
                        <span className="block text-sm font-bold text-primary">{group.label}</span>
                        <span className="text-[10px] text-primary/40">
                          {enabledInGroup}/{groupKeys.length} enabled
                        </span>
                      </span>
                    </button>
                    {canEdit && !locked && (
                      <div className="flex shrink-0 gap-1">
                        <button
                          type="button"
                          onClick={() => toggleGroup(groupKeys, true)}
                          disabled={allOn}
                          className="rounded-md px-2 py-1 text-[10px] font-semibold text-primary/50 hover:bg-primary/[0.04] disabled:opacity-35"
                        >
                          All
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleGroup(groupKeys, false)}
                          disabled={noneOn}
                          className="rounded-md px-2 py-1 text-[10px] font-semibold text-primary/50 hover:bg-primary/[0.04] disabled:opacity-35"
                        >
                          None
                        </button>
                      </div>
                    )}
                  </div>

                  {isOpen && (
                    <div className="border-t border-primary/[0.05] px-2 py-2">
                      <div className="flex flex-wrap gap-1.5">
                        {group.permissions.map(({ key, label }) => {
                          const on = draft.includes(key);
                          return (
                            <button
                              key={key}
                              type="button"
                              title={key}
                              disabled={!canEdit || locked}
                              onClick={() => toggle(key)}
                              className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-left transition-all ${
                                on
                                  ? "border-accent/40 bg-accent/[0.08] text-primary"
                                  : "border-primary/[0.07] bg-primary/[0.015] text-primary/55 hover:border-primary/15"
                              } ${!canEdit || locked ? "cursor-default opacity-80" : "cursor-pointer"}`}
                            >
                              <span
                                className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border ${
                                  on
                                    ? "border-accent bg-accent text-primary"
                                    : "border-primary/20 bg-white text-transparent"
                                }`}
                              >
                                <Check className="h-2.5 w-2.5" strokeWidth={3} />
                              </span>
                              <span className="text-[11px] font-semibold leading-tight">{label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </section>
              );
            })}
          </div>

          <ListPagination
            page={groupsPage}
            totalPages={groupsTotalPages}
            totalItems={groupsTotalItems}
            startIndex={groupsStartIndex}
            endIndex={groupsEndIndex}
            goToPage={goToGroupsPage}
            pageSize={permissionGroupsPageSize}
            alwaysShow
          />
        </div>
      </div>

      <ConfirmModal
        open={Boolean(confirm)}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        tone={confirm?.tone}
        onConfirm={confirm?.onConfirm}
        onClose={() => setConfirm(null)}
      />
    </>
  );
}
